import {
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FormDialogMode,
  RowCustomAction,
  SortDirection,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Block, Edit } from '@material-ui/icons'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCriticidad } from '../../../../api/oData/enums/enumCriticidad'
import { EnumEstadoTarea } from '../../../../api/oData/enums/enumEstadoTarea'
import { enumToSelectOptionArray } from '../../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../hooks'
import { getCustomerDomain } from '../../../../redux'
import { bulkUpdateObjections } from '../../../../services/objections.services'
import { GlobalObjectionTableModel } from '../../settlements.models'

interface GlobalObjectionsDataTableProps {
  title?: string | React.ReactNode
  objectionsData?: GlobalObjectionTableModel[]
  hasEditPermission?: boolean
  tableUserPermissions?: UserPermission[]
  onTerminateObjection?: (row: GlobalObjectionTableModel) => Promise<void>
  /** Enables multiple row selection and bulk terminate toolbar button */
  canBulkTerminate?: boolean
  /** Enables individual and bulk criticality update actions */
  canModifyCriticality?: boolean
}

export const GlobalObjectionsDataTable: React.FC<GlobalObjectionsDataTableProps> = ({
  title,
  objectionsData,
  hasEditPermission = false,
  tableUserPermissions,
  onTerminateObjection,
  canBulkTerminate = false,
  canModifyCriticality = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements'])
  const { info, error } = useNotify()
  const {
    openForm: openCriticalityForm,
    closeForm: closeCriticalityForm,
    formProps: criticalityFormProps,
    CustomFormDialog: CriticalityFormDialog
  } = useFormDialog()

  // #region Handlers Starts

  const handleBulkTerminate = async (selectedRows: GlobalObjectionTableModel[]) => {
    const ids = selectedRows.map(r => r.id!)
    const response = await bulkUpdateObjections({
      objectionIds: ids,
      state: EnumEstadoTarea.Finalizada
    })
    if (!response.success) {
      error(t('settlements:components.globalObjectionsDataTable.bulkTerminate.error'))
    } else {
      info(t('settlements:components.globalObjectionsDataTable.bulkTerminate.success'))
    }
  }

  const handleOpenCriticalityForm = (rows: GlobalObjectionTableModel[]) => {
    pendingCriticalityRowsRef.current = rows
    openCriticalityForm({
      definition: criticalityFormDefinitionRef.current,
      title: t('settlements:components.globalObjectionsDataTable.forms.criticality.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: handleCriticalityFormClose
    })
  }

  const handleCriticalityFormClose = async (formData: any) => {
    if (formData) {
      if (
        !missingFieldsFormWithRequiredText(criticalityFormDefinitionRef.current.sections!, formData)
      ) {
        const ids = pendingCriticalityRowsRef.current.map(r => r.id!)
        const response = await bulkUpdateObjections({
          objectionIds: ids,
          criticality: formData.criticalityId as EnumCriticidad
        })
        if (!response.success) {
          error(t('settlements:components.globalObjectionsDataTable.updateCriticality.error'))
        } else {
          info(t('settlements:components.globalObjectionsDataTable.updateCriticality.success'))
        }
      } else {
        error(t('settlements:components.globalObjectionsDataTable.forms.incompleteForm'))
      }
    }
    closeCriticalityForm()
  }

  // #endregion Handlers Ends

  // #region Form Definition Starts

  const criticalityFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    (): FormDialogDefinitionProps => ({
      id: 'UPDATE_CRITICALITY_GLOBAL',
      sections: [
        {
          inputs: [
            {
              name: 'criticalityId',
              label: t(
                'settlements:components.globalObjectionsDataTable.forms.criticality.inputs.criticality'
              ),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumCriticidad, 'enums:EnumCriticidad'),
              required: true
            }
          ]
        }
      ]
    }),
    [t]
  )

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'criticalityId',
      display: false
    },
    {
      name: 'actions',
      label: t('settlements:components.globalObjectionsDataTable.columns.criticality'),
      type: DataType.HTML,
      columnWidth: 120
    },
    {
      name: 'executionDate',
      label: t('settlements:components.globalObjectionsDataTable.columns.executionDate'),
      type: DataType.TEXT,
      columnWidth: 150
    },
    {
      name: 'type',
      label: t('settlements:components.globalObjectionsDataTable.columns.batchType'),
      type: DataType.CHIPS,
      columnWidth: 150
    },
    {
      name: 'description',
      label: t('settlements:components.globalObjectionsDataTable.columns.description'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'comments',
      label: t('settlements:components.globalObjectionsDataTable.columns.comments'),
      type: DataType.TEXT,
      columnWidth: 200
    }
  ]

  const getCustomRowActions = (): RowCustomAction[] => {
    const rowCustomActions: RowCustomAction[] = []

    if (onTerminateObjection && hasEditPermission) {
      rowCustomActions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('settlements:components.globalObjectionsDataTable.actions.terminate'),
        onClick: onTerminateObjection,
        visibleIf: 'canTerminate'
      })
    }

    if (canModifyCriticality && hasEditPermission) {
      rowCustomActions.push({
        icon: <Edit fontSize="small" />,
        tooltip: t('settlements:components.globalObjectionsDataTable.actions.updateCriticality'),
        onClick: row => handleOpenCriticalityForm([row])
      })
    }

    return rowCustomActions
  }

  const getSelectToolbarActions = (): CustomAction[] => {
    const actions: CustomAction[] = []

    if (canBulkTerminate && hasEditPermission) {
      actions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('settlements:components.globalObjectionsDataTable.actions.terminate'),
        onClick: selectedRows => handleBulkTerminate(selectedRows ?? [])
      })
    }

    if (canModifyCriticality && hasEditPermission) {
      actions.push({
        icon: <Edit fontSize="small" />,
        tooltip: t('settlements:components.globalObjectionsDataTable.actions.updateCriticality'),
        onClick: selectedRows => handleOpenCriticalityForm(selectedRows ?? [])
      })
    }

    return actions
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    download: true,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    customInsertEditModal: true,
    selectableRows: !!(canBulkTerminate || canModifyCriticality),
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlements:components.globalObjectionsDataTable.analytics')
    },
    rowCustomActions: getCustomRowActions(),
    selectToolbarCustomActions: getSelectToolbarActions(),
    sortOrder: {
      name: 'executionDate',
      direction: SortDirection.DESC
    }
  }

  // #endregion DataTable Config Ends

  // #region Hooks Starts

  const criticalityFormDefinitionRef = useRef(criticalityFormDefinition)
  useEffect(() => {
    criticalityFormDefinitionRef.current = criticalityFormDefinition
  }, [criticalityFormDefinition])

  const pendingCriticalityRowsRef = useRef<GlobalObjectionTableModel[]>([])

  // #endregion Hooks Ends

  return (
    <>
      <DataTable title={title} data={objectionsData ?? []} columns={columns} options={options} />
      <CriticalityFormDialog {...criticalityFormProps} />
    </>
  )
}
